import { FC, useCallback } from 'react';
import LoadMore from '~/components/load-more';
import { useAtomValue } from 'jotai';
import { useCategoryData } from '~/models/feed';
import { $FeedData, $FeedDateLoadmore } from 'shared/atoms';
import { ArticleCard } from '~/components/article-card';
import { Loading } from '~/components/loading';
import { useTranslation } from 'react-i18next';
import { DivisibleStack } from '~/components/divisible-stack';
import { NotFound } from '~/components/not-found';
import { useRouteLoader } from '~/utils/use-route-loader';
import { $CurrentPageModule } from '~/models/current-page-module';
import { ModuleType } from '~/utils/module-type';
import { ScopedProvider } from '@lemon8/web-app-shared/ssr';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { ClickPositionPrefix } from '~/utils/click-position';

import './style.scss';

const ARTICLE_TYPE = ['video', 'gallery', 'article'];

export const FeedStreamSection: FC = () => {
  const { t } = useTranslation();

  const { categoryName } = useCategoryData();
  const stream = useAtomValue($FeedData(categoryName));
  const { hasMore, items = [], maxBeHotTime, minBeHotTime } = stream;
  const articles = items.filter((item) => ARTICLE_TYPE.includes(item.articleClass.toLowerCase()));

  const loader = useRouteLoader(
    'stream-loadmore',
    {
      max_behot_time: maxBeHotTime,
      min_behot_time: minBeHotTime,
    },
    {
      atomFamilies: {
        $FeedDateLoadmore,
      },
    },
  );

  const loadData = useCallback(() => {
    if (loader.state === 'idle') {
      loader.load();
    }
    return Promise.resolve({ data: [] });
  }, [loader.state]);

  if (!articles.length && !hasMore) {
    return <NotFound />;
  }

  return (
    <div className="feed-fragment">
      <LoadMore
        loadData={loadData}
        isEnd={!hasMore}
        alwaysShowLoading={true}
        loadingText={<Loading variant="ball" fitParent={true} />}
        distanceToLoad={1000}
        endText={t('no_more_items')}
        render={() => (
          <ScopedProvider
            atomsWithHydration={[
              [$CurrentPageModule, ModuleType.related_article],
              [$CurrentPositionPrefix, ClickPositionPrefix.none],
            ]}
          >
            <DivisibleStack wrapper="feed-article-list" divider="article-list-divider">
              {articles.map((article, rank) => (
                <ArticleCard article={article} key={article.groupId} rank={rank} />
              ))}
            </DivisibleStack>
          </ScopedProvider>
        )}
      />
    </div>
  );
};
